import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import glowMachine from "@/assets/glowsolution-geraet-beauty-nuernberg.jpg";
import alixMachine from "@/assets/alix-secret-twin-haarentfernung-geraet.png";

interface ServiceCardProps {
  id: string;
  title: string;
  description: string;
  cta: string;
  image: string;
  video?: string;
  link: string;
  reverse?: boolean;
  tags?: string[];
}

const ServiceCard = ({
  id,
  title,
  description,
  cta,
  image,
  video,
  link,
  reverse = false,
  tags,
}: ServiceCardProps) => {
  return (
    <TooltipProvider delayDuration={100}>
      <div
        id={id}
        className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
          reverse ? "lg:direction-rtl" : ""
        }`}
      >
        {/* Image */}
        <div
          className={`relative ${
            reverse ? "lg:order-2" : ""
          }`}
        >
          <div className="group relative rounded-2xl overflow-hidden shadow-soft-lg">
            {video ? (
              <video
                src={video}
                poster={image}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-80 lg:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <img
                src={image}
                alt={title}
                className="w-full h-80 lg:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            )}
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>

          {/* GlowSolution Machine - Small offset image for Facials */}
          {id === "facials" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`absolute -bottom-6 ${reverse ? "-left-4 lg:-left-8" : "-right-4 lg:-right-8"} w-28 lg:w-36 cursor-pointer z-10`}>
                  <div className="rounded-xl overflow-hidden shadow-lg border-2 border-background hover:scale-110 transition-transform duration-300 group/machine">
                    <img
                      src={glowMachine}
                      alt="GlowSolution Gerät"
                      className="w-full h-36 lg:h-44 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover/machine:opacity-100 transition-opacity duration-300 flex items-end p-2">
                      <span className="text-[10px] font-medium text-white">GlowSolution</span>
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-background border-border/50 shadow-xl p-3 max-w-[200px]">
                <p className="text-sm font-medium mb-1">GlowSolution</p>
                <p className="text-xs text-muted-foreground">High-Tech 4-in-1 Hydrafacial für sofort strahlende Haut</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* ALIX Secret Twin - Small offset image for Haarentfernung */}
          {id === "haarentfernung" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`absolute -bottom-6 ${reverse ? "-left-4 lg:-left-8" : "-right-4 lg:-right-8"} w-28 lg:w-36 cursor-pointer z-10`}>
                  <div className="rounded-xl overflow-hidden shadow-lg border-2 border-background hover:scale-110 transition-transform duration-300 group/machine">
                    <img
                      src={alixMachine}
                      alt="ALIX Secret Twin Gerät"
                      className="w-full h-36 lg:h-44 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover/machine:opacity-100 transition-opacity duration-300 flex items-end p-2">
                      <span className="text-[10px] font-medium text-white">ALIX Secret Twin</span>
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-background border-border/50 shadow-xl p-3 max-w-[200px]">
                <p className="text-sm font-medium mb-1">ALIX Secret Twin</p>
                <p className="text-xs text-muted-foreground">Modernste IPL-Technologie für sanfte, dauerhafte Haarentfernung</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Content */}
        <div
          className={`flex flex-col gap-4 text-center lg:text-left ${
            reverse ? "lg:order-1 lg:text-right lg:items-end" : "lg:items-start"
          } items-center`}
        >
        <div className="flex flex-col gap-2">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/30 backdrop-blur-sm text-xs tracking-[0.15em] uppercase text-foreground font-medium border border-primary/40 w-fit">
            Service
          </span>
          <h3 className="text-2xl md:text-heading font-serif font-light text-foreground">
            {title}
          </h3>
        </div>

        <p className={`text-base text-muted-foreground font-light leading-relaxed max-w-md ${
          reverse ? "lg:ml-auto" : ""
        }`}>
          {description}
        </p>

        {tags && tags.length > 0 && (
          <div className={`flex flex-wrap gap-2 ${reverse ? "lg:justify-end" : ""}`}>
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <Link to={link} className="inline-block group">
          <Button
            variant="outline"
            size="default"
            className="border-foreground/20 hover:border-highlight hover:bg-highlight/10 hover:text-highlight rounded-full px-5 font-medium transition-all duration-300"
          >
            {cta}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ServiceCard;
